import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTheoryProgress } from '../../hooks/useTheoryProgress';
import { useVideoProgress } from '../../hooks/useVideoProgress';

const Strings: React.FC = () => {
  const navigate = useNavigate();
  useTheoryProgress('strings');
  const videoRef = useVideoProgress('strings');

  const handleBackClick = () => {
    navigate('/topics');
  };

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      {/* Minimal grid background */}
      <div className="fixed inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[length:64px_64px] [background-position-x:center]"></div>
      
      <div className="relative z-10 max-w-5xl mx-auto px-4 py-8">
        {/* Header with back button */}
        <div className="flex items-center justify-between mb-8">
          <motion.button
            onClick={handleBackClick}
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
            whileHover={{ x: -5 }}
            whileTap={{ scale: 0.95 }}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span className="text-sm font-medium">Back to Topics</span>
          </motion.button>
        </div>

        {/* Title and description */}
        <motion.div 
          className="mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-normal text-white mb-2">String Manipulation</h1>
          <p className="text-white/70 text-lg">Master string manipulation techniques, algorithms, and optimizations.</p>
        </motion.div>

        <div className="space-y-12">
          {/* Introduction */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="prose prose-invert max-w-none"
          >
            <h2 className="text-2xl font-semibold text-white mb-4">What Are Strings?</h2>
            <div className="text-white/70 space-y-4">
              <p>
                Strings are sequences of characters representing text data, stored contiguously in memory. 
                In JavaScript, they're immutable—once created, they can't be modified in place, making operations create new strings.
              </p>
              <p>
                Think of a string as a chain of beads: each bead is a character, linked in order, 
                and you can examine or combine them, but the chain itself remains fixed.
              </p>
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6 mt-6">
                <h3 className="text-xl font-semibold text-white mb-3">Core Characteristics</h3>
                <ul className="list-disc list-inside text-white/70 space-y-2">
                  <li><strong>Immutability:</strong> Content fixed after creation</li>
                  <li><strong>Encoding:</strong> UTF-16 in JavaScript (2 bytes/char)</li>
                  <li><strong>Indexing:</strong> Zero-based character access</li>
                  <li><strong>Contiguity:</strong> Sequential memory layout</li>
                </ul>
              </div>
            </div>
          </motion.section>

          {/* String Fundamentals */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="prose prose-invert max-w-none"
          >
            <h2 className="text-2xl font-semibold text-white mb-6">String Fundamentals</h2>
            <div className="space-y-6">
              {/* Creation Methods */}
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">Creation Methods</h3>
                  <div className="text-2xl">✍️</div>
                </div>
                <p className="text-white/70 mb-4">
                  Various ways to initialize strings in JavaScript.
                </p>
                <div className="bg-white/5 p-4 rounded-lg">
                  <pre className="text-sm text-white/80 font-mono">
                    <code>{`// Direct creation
const str1 = "Hello";

// From character array
const str2 = ['H', 'i'].join(''); // "Hi"

// From other types
const str3 = String(123); // "123"

// Template literals
const name = "Alice";
const str4 = \`Hello, \${name}\`; // "Hello, Alice"`}</code>
                  </pre>
                </div>
              </div>

              {/* Access and Iteration */}
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">Access and Iteration</h3>
                  <div className="text-2xl">🔎</div>
                </div>
                <p className="text-white/70 mb-4">
                  Techniques to read and traverse string content.
                </p>
                <div className="bg-white/5 p-4 rounded-lg">
                  <pre className="text-sm text-white/80 font-mono">
                    <code>{`const str = "Hello";

// Access by index (O(1))
const char = str[1]; // "e"

// Substring extraction
const sub = str.slice(1, 4); // "ell"

// Length
const len = str.length; // 5

// Iteration
for (const char of str) {
  console.log(char); // H, e, l, l, o
}`}</code>
                  </pre>
                </div>
              </div>
            </div>
          </motion.section>

          {/* String Manipulation */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="prose prose-invert max-w-none"
          >
            <h2 className="text-2xl font-semibold text-white mb-6">String Manipulation</h2>
            <div className="space-y-6">
              {/* Search and Replace */}
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">Search and Replace</h3>
                  <div className="text-2xl">🔍</div>
                </div>
                <p className="text-white/70 mb-4">
                  Methods to locate and modify string content.
                </p>
                <div className="bg-white/5 p-4 rounded-lg">
                  <pre className="text-sm text-white/80 font-mono">
                    <code>{`const str = "Hello World";

// Find index
const index = str.indexOf("World"); // 6

// Check existence
const hasHello = str.includes("Hello"); // true

// Replace
const newStr = str.replace("World", "Universe"); // "Hello Universe"

// Split into array
const words = str.split(" "); // ["Hello", "World"]`}</code>
                  </pre>
                </div>
              </div>

              {/* Transformation */}
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">Transformation</h3>
                  <div className="text-2xl">✨</div>
                </div>
                <p className="text-white/70 mb-4">
                  Operations to alter string appearance or structure.
                </p>
                <div className="bg-white/5 p-4 rounded-lg">
                  <pre className="text-sm text-white/80 font-mono">
                    <code>{`const str = "  Hello World  ";

// Case conversion
const upper = str.toUpperCase(); // "  HELLO WORLD  "
const lower = str.toLowerCase(); // "  hello world  "

// Trim whitespace
const trimmed = str.trim(); // "Hello World"

// Reverse (via array)
const reversed = str.split('').reverse().join(''); // "  dlroW olleH  "`}</code>
                  </pre>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Advanced Algorithms */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="prose prose-invert max-w-none"
          >
            <h2 className="text-2xl font-semibold text-white mb-6">Advanced Algorithms</h2>
            <div className="space-y-6">
              {/* KMP Pattern Matching */}
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">KMP Pattern Matching</h3>
                  <div className="text-2xl">🔗</div>
                </div>
                <p className="text-white/70 mb-4">
                  Efficiently finds pattern occurrences using a longest prefix-suffix array.
                </p>
                <div className="bg-white/5 p-4 rounded-lg">
                  <pre className="text-sm text-white/80 font-mono">
                    <code>{`function buildLPS(pattern: string): number[] {
  const lps = new Array(pattern.length).fill(0);
  let len = 0;
  let i = 1;
  
  while (i < pattern.length) {
    if (pattern[i] === pattern[len]) {
      len++;
      lps[i] = len;
      i++;
    } else if (len > 0) {
      len = lps[len - 1];
    } else {
      lps[i] = 0;
      i++;
    }
  }
  return lps;
}

function kmpSearch(text: string, pattern: string): number[] {
  const lps = buildLPS(pattern);
  const result: number[] = [];
  let i = 0; // text index
  let j = 0; // pattern index
  
  while (i < text.length) {
    if (pattern[j] === text[i]) {
      i++;
      j++;
    }
    if (j === pattern.length) {
      result.push(i - j);
      j = lps[j - 1];
    } else if (i < text.length && pattern[j] !== text[i]) {
      if (j > 0) j = lps[j - 1];
      else i++;
    }
  }
  return result;
}

console.log(kmpSearch("ababcababc", "ababc")); // [0, 5]`}</code>
                  </pre>
                </div>
              </div>

              {/* Edit Distance */}
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">Edit Distance</h3>
                  <div className="text-2xl">📏</div>
                </div>
                <p className="text-white/70 mb-4">
                  Calculates minimum edits (insert, delete, substitute) to transform one string into another.
                </p>
                <div className="bg-white/5 p-4 rounded-lg">
                  <pre className="text-sm text-white/80 font-mono">
                    <code>{`function editDistance(str1: string, str2: string): number {
  const dp = Array(str1.length + 1).fill(0).map(() => Array(str2.length + 1).fill(0));
  
  // Initialize first row and column
  for (let i = 0; i <= str1.length; i++) dp[i][0] = i;
  for (let j = 0; j <= str2.length; j++) dp[0][j] = j;
  
  // Fill dp table
  for (let i = 1; i <= str1.length; i++) {
    for (let j = 1; j <= str2.length; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = 1 + Math.min(
          dp[i - 1][j],   // Deletion
          dp[i][j - 1],   // Insertion
          dp[i - 1][j - 1] // Substitution
        );
      }
    }
  }
  return dp[str1.length][str2.length];
}

console.log(editDistance("cat", "cut")); // 1
console.log(editDistance("sunday", "saturday")); // 3`}</code>
                  </pre>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Performance Considerations */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <h2 className="text-2xl font-semibold text-white mb-4">Performance Considerations</h2>
            <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
              <p className="text-white/70 mb-4">
                Key factors affecting string operation efficiency:
              </p>
              <ul className="list-disc list-inside text-white/70 space-y-4">
                <li>
                  <strong>Immutability Overhead:</strong> Each modification creates a new string.
                  <div className="bg-white/5 p-4 rounded-lg mt-2">
                    <pre className="text-sm text-white/80 font-mono">
                      <code>{`let str = "Hello";
str += " World"; // Creates new string, O(n+m)`}</code>
                    </pre>
                  </div>
                </li>
                <li>
                  <strong>Time Complexity:</strong> Varies by operation.
                  <div className="bg-white/5 p-4 rounded-lg mt-2">
                    <pre className="text-sm text-white/80 font-mono">
                      <code>{`const str = "Hello World";
// Access: O(1)
const char = str[0];
// Concatenation: O(n+m)
const combined = str + "!";
// Search: O(n)
const idx = str.indexOf("World");`}</code>
                    </pre>
                  </div>
                </li>
                <li>
                  <strong>Optimization:</strong> Use StringBuilder-like approaches for heavy concatenation.
                  <div className="bg-white/5 p-4 rounded-lg mt-2">
                    <pre className="text-sm text-white/80 font-mono">
                      <code>{`// Efficient concatenation
const parts = ["Hello", "World"];
const result = parts.join(" "); // O(n) once vs O(n) per +`}</code>
                    </pre>
                  </div>
                </li>
              </ul>
            </div>
          </motion.section>

          {/* Video Tutorial */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="bg-white/5 rounded-lg p-6"
          >
            <h2 className="text-2xl font-semibold text-white mb-4">Video Tutorial</h2>
            <div className="aspect-w-16 aspect-h-9 bg-black/50 rounded-lg overflow-hidden">
                <video
                  ref={videoRef}
                  className="w-full h-full object-cover"
                  controls
                  playsInline
                >
                  <source src="https://files.catbox.moe/30vlq3.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
            </div>
            <p className="text-white/70 mt-4 text-center">
              Watch this video for a visual understanding of string manipulation and algorithms.
            </p>
          </motion.section>

          {/* Why It Matters */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2 }}
          >
            <h2 className="text-2xl font-semibold text-white mb-4">Why Strings Matter</h2>
            <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
              <ul className="space-y-4 text-white/70">
                <li className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">✓</span>
                  <span><strong>Ubiquity:</strong> Essential for text processing in all applications.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">✓</span>
                  <span><strong>Efficiency:</strong> Fast access and manipulation with proper techniques.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">✓</span>
                  <span><strong>Algorithms:</strong> Foundation for pattern matching and text analysis.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">✓</span>
                  <span><strong>Real-World Use:</strong> Critical for parsing, formatting, and validation.</span>
                </li>
              </ul>
            </div>
          </motion.section>

          {/* Next Steps */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4 }}
          >
            <h2 className="text-2xl font-semibold text-white mb-4">Next Steps</h2>
            <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
              <p className="text-white/70 mb-4">
                With string manipulation skills, you're ready to:
              </p>
              <ul className="list-disc list-inside text-white/70 space-y-2">
                <li>Implement string processing algorithms</li>
                <li>Optimize text operations</li>
                <li>Explore advanced pattern matching</li>
                <li>Practice real-world challenges</li>
              </ul>
              <div className="mt-6 flex justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 transition-colors"
                  onClick={() => navigate('/practice/strings')}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <span>Start Practice</span>
                </motion.button>
              </div>
            </div>
          </motion.section>
        </div>
      </div>
    </div>
  );
};

export default Strings;